import { useEffect, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { RichEditor, tiptapDocToPlainText } from '@odo/shared-ui';
import type { RichEditorImageUploadHandler, TiptapDoc } from '@odo/shared-ui';
import {
    MAX_NOTE_BODY_LENGTH,
    MAX_NOTE_TITLE_LENGTH,
    type SeverityKey
} from '../../../patient-files/notes/severity';
import type {
    CommunicationEntry,
    CommunicationPatientRef
} from '../../../../services/communications';
import { AddPatientNoteFooter } from './AddPatientNoteFooter';
import { AddPatientNotePatientsField } from './AddPatientNotePatientsField';
import { AddPatientNoteSeverityToggle } from './AddPatientNoteSeverityToggle';
import { DiscardChangesConfirmModal } from './DiscardChangesConfirmModal';
import { patientNoteDocFromEntry } from './patientNoteFromEntry';
import { useCreatePatientNotes } from './useCreatePatientNotes';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';

// No image upload here: the doc is shared across every selected patient
// until save-time fan-out, and internal image storage is patient-scoped -
// there's no single patient to attach a fresh upload to yet.
const NO_IMAGE_UPLOAD: RichEditorImageUploadHandler = () =>
    Promise.reject(new Error('Images cannot be added to a note created from a communication.'));

const PATIENTS_LABEL_ID = 'add-patient-note-patients-label';
const SEVERITY_LABEL_ID = 'add-patient-note-severity-label';
const TITLE_INPUT_ID = 'add-patient-note-title';

function truncate(value: string, max: number): string {
    return value.length > max ? value.slice(0, max) : value;
}

interface AddPatientNoteModalProps {
    entry: CommunicationEntry;
    communicationId: string;
    communicationTitle: string;
    /** Null on a practice-less thread - restricts the picker to `linkedPatients` only. */
    practiceId: string | null;
    linkedPatients: CommunicationPatientRef[];
    onClose: () => void;
}

/**
 * Creates one or more patient notes from an interaction's content - opened
 * from EntryRowMenu's "Add patient note". Save orchestration (auto-link,
 * fan-out, toast) lives in useCreatePatientNotes; this component owns the
 * form fields and chrome.
 */
export function AddPatientNoteModal({
    entry,
    communicationId,
    communicationTitle,
    practiceId,
    linkedPatients,
    onClose
}: AddPatientNoteModalProps) {
    const {
        selectedPatients,
        setSelectedPatients,
        initialSelectedPatients,
        alreadyNotedPatients,
        alreadyNotedIds,
        isSaving,
        handleSave
    } = useCreatePatientNotes({ entry, communicationId, linkedPatients });

    const initialTitle = truncate(entry.title || communicationTitle, MAX_NOTE_TITLE_LENGTH);
    const initialDoc = patientNoteDocFromEntry(entry);

    const [severity, setSeverity] = useState<SeverityKey>('info');
    const [title, setTitle] = useState(initialTitle);
    const [doc, setDoc] = useState<TiptapDoc>(initialDoc);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

    const titleRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        titleRef.current?.focus();
    }, []);

    const plainText = tiptapDocToPlainText(doc).trim();
    const overLimit = plainText.length > MAX_NOTE_BODY_LENGTH;
    const canSave =
        selectedPatients.length > 0 &&
        title.trim().length > 0 &&
        plainText.length > 0 &&
        !overLimit &&
        !isSaving;

    const isDirty =
        severity !== 'info' ||
        title !== initialTitle ||
        plainText !== tiptapDocToPlainText(initialDoc).trim() ||
        selectedPatients.length !== initialSelectedPatients.length ||
        selectedPatients.some((p) => !initialSelectedPatients.some((ip) => ip.id === p.id));

    const requestClose = () => {
        if (isSaving) return;
        if (isDirty) {
            setShowDiscardConfirm(true);
            return;
        }
        onClose();
    };

    // Suspended while the discard confirm is up - that modal owns Escape for the
    // top layer, so the two document listeners never both act on one keypress.
    useEscapeKey(() => {
        // The picker owns this Escape first - closing just the picker,
        // not the whole modal, matches PatientPicker's own search-input
        // handler and every other floating picker in the app.
        if (pickerOpen) {
            setPickerOpen(false);
            return;
        }
        requestClose();
    }, !showDiscardConfirm);

    const handlePick = (patient: CommunicationPatientRef) => {
        setPickerOpen(false);
        if (selectedPatients.some((p) => p.id === patient.id)) return;
        setSelectedPatients((prev) => [...prev, patient]);
    };

    const handleRemove = (patientId: string) => {
        setSelectedPatients((prev) => prev.filter((p) => p.id !== patientId));
    };

    const handleSaveClick = async () => {
        if (!canSave) return;
        const fullySucceeded = await handleSave({ severity, title, doc });
        if (fullySucceeded) onClose();
    };

    const saveLabel =
        selectedPatients.length > 1 ? `Create ${selectedPatients.length} notes` : 'Create note';

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-patient-note-modal-title"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        >
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col overflow-hidden"
                style={{ maxHeight: '90vh' }}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h2
                        id="add-patient-note-modal-title"
                        className="text-sm font-bold text-gray-900"
                    >
                        Add patient note
                    </h2>
                    <button
                        onClick={requestClose}
                        disabled={isSaving}
                        aria-label="Close"
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 disabled:opacity-50"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto px-5 py-4 flex flex-col gap-4">
                    <div>
                        <div
                            id={PATIENTS_LABEL_ID}
                            className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.06em] mb-1.5"
                        >
                            Patients
                        </div>
                        <AddPatientNotePatientsField
                            labelId={PATIENTS_LABEL_ID}
                            selectedPatients={selectedPatients}
                            alreadyNotedPatients={alreadyNotedPatients}
                            alreadyNotedIds={alreadyNotedIds}
                            linkedPatients={linkedPatients}
                            practiceId={practiceId}
                            isSaving={isSaving}
                            pickerOpen={pickerOpen}
                            onTogglePicker={() => setPickerOpen((v) => !v)}
                            onClosePicker={() => setPickerOpen(false)}
                            onPick={handlePick}
                            onRemove={handleRemove}
                        />
                    </div>

                    <div>
                        <div
                            id={SEVERITY_LABEL_ID}
                            className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.06em] mb-2"
                        >
                            Severity
                        </div>
                        <AddPatientNoteSeverityToggle
                            value={severity}
                            onChange={setSeverity}
                            labelId={SEVERITY_LABEL_ID}
                        />
                    </div>

                    <div>
                        <label
                            htmlFor={TITLE_INPUT_ID}
                            className="block text-[11px] font-bold text-gray-400 uppercase tracking-[0.06em] mb-1.5"
                        >
                            Title
                        </label>
                        <input
                            ref={titleRef}
                            id={TITLE_INPUT_ID}
                            type="text"
                            value={title}
                            maxLength={MAX_NOTE_TITLE_LENGTH}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Short, descriptive title"
                            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-300 outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100"
                        />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.06em]">
                                Note
                            </div>
                            <span
                                className={`text-[10px] ${overLimit ? 'text-red-500' : 'text-gray-300'}`}
                            >
                                {plainText.length}/{MAX_NOTE_BODY_LENGTH}
                            </span>
                        </div>
                        <div aria-label="Note body">
                            <RichEditor
                                value={doc}
                                onChange={setDoc}
                                onImageUpload={NO_IMAGE_UPLOAD}
                                placeholder="Add context, instructions, or observations"
                                minHeightPx={140}
                            />
                        </div>
                    </div>
                </div>

                <AddPatientNoteFooter
                    isSaving={isSaving}
                    canSave={canSave}
                    saveLabel={saveLabel}
                    onCancel={requestClose}
                    onSave={() => void handleSaveClick()}
                />
            </div>

            {showDiscardConfirm && (
                <DiscardChangesConfirmModal
                    description="This note has unsaved changes. If you continue now they will be lost."
                    onKeepEditing={() => setShowDiscardConfirm(false)}
                    onDiscard={onClose}
                />
            )}
        </div>
    );
}
